<template>
    <Dialog :header="dialogTitle" v-model:visible="visible"
        :style="{ width: '500px' }" :modal="true" @keyup.enter="onConfirm">
        <div class="confirmation-content">
            <CustomForm :fields="formFields" v-model="data" />
        </div>

        <template #footer>
            <Button rounded label="Cancel" icon="pi pi-times" @click="onCancel" class="p-button-text" data-cy="workspaceNameCancel" />
            <Button rounded :disabled="creatingElement" :label="confirmButtonLabel" icon="pi pi-check" @click.once="onConfirm" data-cy="workspaceNameConfirm" />
        </template>
    </Dialog>
</template>

<script setup>
import {ref, computed, watch, onMounted} from 'vue';
import Button from 'primevue/button';
import {useStore} from 'vuex';
import WorkspaceModel from "@/src/model/WorkspaceModel";

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    editElement: WorkspaceModel,
    parent: WorkspaceModel
});

const visible = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
});

const emit = defineEmits(['update:modelValue', 'confirm']);

const store = useStore();

const data = ref({
    name: '',
    datamodel_id: '',
    datapool_id: null,
});

onMounted(() => {
    if (props.editElement) {
        data.value.name = props.editElement.name || '';
        data.value.datamodel_id = props.editElement.datapool_id && props.editElement.datamodel_id ? props.editElement.datamodel_id : null;
        data.value.datapool_id = props.editElement.datapool_id || null;
    } else {
        data.value.name = '';
        data.value.datamodel_id = props.parent && props.parent.datapool_id && props.parent.datamodel_id ? props.parent.datamodel_id : null;
        data.value.datapool_id = props.parent && props.parent.datapool_id ? props.parent.datapool_id : null;
    }
})

const error = ref({});
const creatingElement = ref(false);

const type = computed(() => props.editElement?.parent ? 'Folder' : 'Process');

const dialogTitle = computed(() => props.editElement ? `Edit ${type.value}` : `Create ${type.value}`);
const confirmButtonLabel = computed(() => props.editElement ? `Update` : `Create`);

const initialDatapoolId = computed(() => {
    return props.editElement ? props.editElement.datapool_id : (props.parent ? props.parent.datapool_id : null);
});

const formFields = computed(() => [
    {
        elementName: 'InputText',
        dataField: 'name',
        type: 'text',
        label: `${type.value} Name`,
        required: true,
        invalid: error.value && error.value.name,
        invalidMessage: error.value.name,
    },
    {
        elementName: 'DatamodelSelector',
        dataField: 'datamodel_id',
        label: 'Datamodel',
        infoTooltipRight: 'Optional, this datamodel can be used as a default for new graphs',
        invalid: error.value && error.value.datamodel_id,
        invalidMessage: error.value.datamodel_id,
        initialDatapoolId: initialDatapoolId.value
    },
]);

watch(() => props.editElement, (newVal) => {
    if (newVal) {
        data.value.name = newVal.name || '';
        data.value.datamodel_id = newVal.datapool_id && newVal.datamodel_id ? newVal.datamodel_id : null;
    } else {
        data.value.name = '';
        data.value.datamodel_id = props.parent && props.parent.datapool_id && props.parent.datamodel_id ? props.parent.datamodel_id : null;
    }
}, {immediate: true});

const onCancel = () => {
    emit('update:modelValue', false);
};

const findDatapool = datamodelId => store.getters["repoStore/findDatapool"](datamodelId)

const onConfirm = async () => {
    if (creatingElement.value) return;
    creatingElement.value = true;

    const payload = { name: data.value.name };
    if (data.value.datamodel_id) {
        payload.datamodel_id = data.value.datamodel_id;
        payload.datapool_id = findDatapool(data.value.datamodel_id);
    }

    try {
        if (props.editElement) {
            await store.dispatch('repoStore/updateWorkspace', {
                workspaceId: props.editElement.id,
                data: payload,
            });
        } else {
            payload.parent = props.parent ? props.parent.id : null;
            await store.dispatch('repoStore/addSpace', payload);
        }
        emit('confirm');
        emit('update:modelValue', false);
    } catch (e) {
        error.value = e.response.data.error.field_errors;
    } finally {
        creatingElement.value = false;
    }
};
</script>